import { useCurrentFrame, interpolate, Easing } from 'remotion';

// Constants
import { CLAMP_BOTH, VIDEO_CONFIG } from '@/constants';

// Types
export enum PushDirection {
  LEFT = 'left',
  RIGHT = 'right',
  UP = 'up',
  DOWN = 'down',
}

export interface PushTransitionConfig {
  /** Frame when the transition should start */
  startFrame: number;
  /** Duration of the transition in frames */
  duration?: number;
  /** Direction the outgoing scene moves */
  direction?: PushDirection;
  /** Custom easing function */
  easing?: (t: number) => number;
}

export interface PushTransitionResult {
  /** Progress of the transition (0 to 1) */
  progress: number;
  /** Transform style for the outgoing scene */
  outgoingStyle: React.CSSProperties;
  /** Transform style for the incoming scene */
  incomingStyle: React.CSSProperties;
  /** Whether the transition has started */
  isTransitioning: boolean;
  /** Whether the transition has completed */
  isComplete: boolean;
}

const DEFAULT_DURATION = 40; // ~1.3 seconds at 30fps

/**
 * Creates a push/slide transition effect between two scenes in Remotion.
 *
 * This hook calculates the transform styles needed to animate two scenes sliding
 * in a specified direction. The outgoing scene slides out while the incoming scene
 * slides in simultaneously, creating a smooth push transition effect.
 *
 * @param config - Configuration object for the transition
 * @param config.startFrame - The frame number when the transition should start
 * @param config.duration - Duration of the transition in frames (default: 40 frames, ~1.3s at 30fps)
 * @param config.direction - Direction the outgoing scene moves: 'left', 'right', 'up', or 'down' (default: 'left')
 * @param config.easing - Custom easing function for the transition (default: Easing.inOut(Easing.cubic))
 */
export const usePushTransition = ({
  startFrame,
  duration = DEFAULT_DURATION,
  direction = PushDirection.LEFT,
  easing = Easing.inOut(Easing.cubic),
}: PushTransitionConfig): PushTransitionResult => {
  const frame = useCurrentFrame();

  // Calculate transition progress
  const progress = interpolate(
    frame,
    [startFrame, startFrame + duration],
    [0, 1],
    {
      ...CLAMP_BOTH,
      easing,
    },
  );

  // Calculate translation based on direction
  const getTranslation = (
    isOutgoing: boolean,
  ): { translateX: number; translateY: number } => {
    const { width, height } = VIDEO_CONFIG;

    switch (direction) {
      case PushDirection.LEFT:
        return {
          translateX: isOutgoing
            ? interpolate(progress, [0, 1], [0, -width])
            : interpolate(progress, [0, 1], [width, 0]),
          translateY: 0,
        };
      case PushDirection.RIGHT:
        return {
          translateX: isOutgoing
            ? interpolate(progress, [0, 1], [0, width])
            : interpolate(progress, [0, 1], [-width, 0]),
          translateY: 0,
        };
      case PushDirection.UP:
        return {
          translateX: 0,
          translateY: isOutgoing
            ? interpolate(progress, [0, 1], [0, -height])
            : interpolate(progress, [0, 1], [height, 0]),
        };
      case PushDirection.DOWN:
        return {
          translateX: 0,
          translateY: isOutgoing
            ? interpolate(progress, [0, 1], [0, height])
            : interpolate(progress, [0, 1], [-height, 0]),
        };
      default:
        return { translateX: 0, translateY: 0 };
    }
  };

  const outgoingTranslation = getTranslation(true);
  const incomingTranslation = getTranslation(false);

  return {
    progress,
    outgoingStyle: {
      transform: `translate(${outgoingTranslation.translateX}px, ${outgoingTranslation.translateY}px)`,
    },
    incomingStyle: {
      transform: `translate(${incomingTranslation.translateX}px, ${incomingTranslation.translateY}px)`,
    },
    isTransitioning: progress > 0 && progress < 1,
    isComplete: progress >= 1,
  };
};
